import React from 'react';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { EyeIcon } from '@heroicons/react/24/outline';
import {
  ASSIGNMENT_STATUS_LABELS,
  ASSIGNMENT_PRIORITY_LABELS,
  TASK_TYPE_LABELS,
} from '@claimup/shared/constants/assignments';
import { expedientStatusLabel } from '@claimup/shared/constants/expedient';
import { assigneeName, visibleNotes } from '../../../utils/assignmentLabels';

const formatAmount = (amount, currency) => {
  if (typeof amount !== 'number') return '—';
  try {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: currency || 'EUR' }).format(amount);
  } catch {
    return `${amount} ${currency || ''}`.trim();
  }
};

const AssignmentsList = ({
  assignments,
  isSelected,
  onToggleOne,
  // La edición en bloque es del admin (`/assignments/bulk`): al resto no se le
  // ofrece seleccionar.
  selectable = true,
  // El cliente de cada fila: al admin le sirve; a una empresa, que solo ve lo
  // suyo, es una columna que repite su nombre.
  showClient = true,
  headerChecked,
  headerIndeterminate,
  onToggleAll,
}) => {
  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    in_progress: 'bg-blue-100 text-blue-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-gray-100 text-gray-800',
    failed: 'bg-red-100 text-red-800',
  };

  const priorityColors = {
    low: 'bg-slate-100 text-slate-700',
    medium: 'bg-blue-100 text-blue-700',
    high: 'bg-orange-100 text-orange-700',
    urgent: 'bg-red-100 text-red-700',
  };

  const isOverdue = assignment => {
    return new Date(assignment.date) < new Date() && assignment.status === 'pending';
  };

  // El checkbox de cabecera selecciona TODOS los que cumplen el filtro, no solo
  // los de esta página. El estado indeterminado refleja una selección parcial.
  const headerRef = React.useRef(null);
  React.useEffect(() => {
    if (headerRef.current) headerRef.current.indeterminate = headerIndeterminate;
  }, [headerIndeterminate]);

  return (
    <div className='overflow-x-auto'>
      <table className='table-auto w-full'>
        <thead className='text-xs font-semibold uppercase text-slate-500 bg-slate-50 border-t border-b border-slate-200'>
          <tr>
            {selectable && (
              <th className='px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap w-10'>
                <input
                  ref={headerRef}
                  type='checkbox'
                  className='form-checkbox rounded-lg border-slate-300 text-primary-500 focus:ring-primary-500 cursor-pointer'
                  checked={headerChecked}
                  onChange={onToggleAll}
                  title='Seleccionar todos los que cumplen el filtro'
                  aria-label='Seleccionar todos los que cumplen el filtro'
                />
              </th>
            )}
            <th className='px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap'>
              <div className='font-semibold text-left'>Descripción</div>
            </th>
            <th className='px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap'>
              <div className='font-semibold text-left'>Deudor</div>
            </th>
            {showClient && (
              <th className='px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap'>
                <div className='font-semibold text-left'>Cliente</div>
              </th>
            )}
            <th className='px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap'>
              <div className='font-semibold text-left'>Estado exp.</div>
            </th>
            <th className='px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap'>
              <div className='font-semibold text-right'>Importe</div>
            </th>
            <th className='px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap'>
              <div className='font-semibold text-left'>Tipo</div>
            </th>
            <th className='px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap'>
              <div className='font-semibold text-left'>Estado</div>
            </th>
            <th className='px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap'>
              <div className='font-semibold text-left'>Prioridad</div>
            </th>
            <th className='px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap'>
              <div className='font-semibold text-left'>Fecha</div>
            </th>
            <th className='px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap'>
              <div className='font-semibold text-left'>Asignado a</div>
            </th>
            <th className='px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap w-10'>{/* Icono sin título */}</th>
          </tr>
        </thead>
        <tbody className='text-sm divide-y divide-slate-200'>
          {assignments.map(assignment => {
            const checked = isSelected(assignment._id);
            const expediente = assignment.expediente;
            const marcaAparte =
              expediente?.cliente && expediente?.nombre_comercial && expediente.nombre_comercial !== expediente.cliente;
            return (
              <tr key={assignment._id} className={checked ? 'bg-primary-50' : 'hover:bg-slate-50'}>
                {selectable && (
                  <td className='px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap'>
                    <input
                      type='checkbox'
                      className='form-checkbox rounded-lg border-slate-300 text-primary-500 focus:ring-primary-500 cursor-pointer'
                      checked={checked}
                      onChange={() => onToggleOne(assignment._id)}
                      aria-label={`Seleccionar ${assignment.description}`}
                    />
                  </td>
                )}
                {/* La descripción hace salto de línea: en una sola, la tabla se salía por la derecha. */}
                <td className='px-2 first:pl-5 last:pr-5 py-3 min-w-[16rem] max-w-md'>
                  <div className='font-medium text-slate-800'>{assignment.description}</div>
                  {visibleNotes(assignment.notes) && (
                    <div className='text-xs text-slate-500 mt-1'>{visibleNotes(assignment.notes)}</div>
                  )}
                </td>
                <td className='px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap'>
                  <div className='text-slate-800'>
                    {expediente?.deudor || <span className='text-slate-400'>—</span>}
                  </div>
                  {expediente?.deudores_count > 1 && (
                    <div className='text-xs text-slate-500'>+{expediente.deudores_count - 1} más</div>
                  )}
                </td>
                {showClient && (
                  <td className='px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap'>
                    <div className='text-slate-700'>
                      {expediente?.cliente || expediente?.nombre_comercial || <span className='text-slate-400'>—</span>}
                    </div>
                    {marcaAparte && <div className='text-xs text-slate-500'>{expediente.nombre_comercial}</div>}
                  </td>
                )}
                <td className='px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap'>
                  <div className='text-slate-700 text-xs'>
                    {expedientStatusLabel(expediente?.estado) || <span className='text-slate-400'>—</span>}
                  </div>
                </td>
                <td className='px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap text-right'>
                  <div className='text-slate-800 font-medium'>
                    {formatAmount(expediente?.importe_deuda, expediente?.divisa)}
                  </div>
                </td>
                <td className='px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap'>
                  <div className='text-slate-700 text-xs'>{TASK_TYPE_LABELS[assignment.task_type]}</div>
                </td>
                <td className='px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap'>
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      statusColors[assignment.status]
                    }`}
                  >
                    {ASSIGNMENT_STATUS_LABELS[assignment.status]}
                  </span>
                </td>
                <td className='px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap'>
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      priorityColors[assignment.priority]
                    }`}
                  >
                    {ASSIGNMENT_PRIORITY_LABELS[assignment.priority]}
                  </span>
                </td>
                <td className='px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap'>
                  <div className={`text-sm ${isOverdue(assignment) ? 'text-red-600 font-semibold' : 'text-slate-700'}`}>
                    {moment(assignment.date).format('DD/MM/YYYY HH:mm')}
                  </div>
                  {isOverdue(assignment) && <div className='text-xs text-red-500'>Vencida</div>}
                </td>
                <td className='px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap'>
                  {assignment.assigned_to ? (
                    <div className='text-slate-700'>{assigneeName(assignment.assigned_to)}</div>
                  ) : (
                    <span className='text-slate-400 text-sm'>Sin asignar</span>
                  )}
                </td>
                <td className='px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap text-center'>
                  {assignment.expedient_id ? (
                    <Link
                      to={`/view/${
                        assignment.expedient_id._id || assignment.expedient_id.expedient_id || assignment.expedient_id
                      }`}
                      className='text-primary-500 hover:text-primary-600 transition-colors inline-flex items-center justify-center'
                      title='Ver expediente'
                    >
                      <EyeIcon className='w-5 h-5' />
                    </Link>
                  ) : (
                    <span className='text-slate-300'>
                      <EyeIcon className='w-5 h-5' />
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default AssignmentsList;
